import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  ClientProfile,
  ClientListResponse,
  ClientStatusCount,
  CreateClientRequest,
  UpdateClientRequest,
  ClientBehandelaarResponse
} from '@/api/generated-clients'
import { initApiService } from '@/services/init'
import { getGlobalT } from '@/i18n'
import { extractApiErrorMessage, isConflictError, isNetworkError, logUnexpectedFailure } from '@/utils/errorHandling'

/**
 * Clients Store
 *
 * Manages client data with pagination, search, filtering, and CRUD operations.
 * All operations are scoped to the authenticated user's organization.
 *
 * `loading` and `error` describe the **list load** only — `ClientsView` hands
 * them to a `DataTable`, which renders each of them *instead of* the rows, so
 * anything else writing there blanks the whole list (#3398, #3407). The
 * mutations and the single-client load therefore only rethrow; every caller owns
 * an error surface next to the action that failed (`useCreateClientSubmit`,
 * `useClientInformation`, `useDossierContext`, `ClientBehandelaarsManager`).
 */
export const useClientsStore = defineStore('clients', () => {
  const t = getGlobalT()

  // State - Client list
  const clients = ref<ClientProfile[]>([])
  /** True while {@link fetchClients} is in flight. */
  const loading = ref(false)
  /** Message of the last failed {@link fetchClients}. */
  const error = ref<string | null>(null)

  // State - Pagination
  const totalElements = ref(0)
  const totalPages = ref(0)
  const currentPage = ref(0)
  const pageSize = ref(20)
  const totalUnfilteredCount = ref(0)

  // State - Search and filters
  const searchQuery = ref('')
  const sortField = ref('lastName')
  const sortDirection = ref<'asc' | 'desc'>('asc')
  const locationIdFilter = ref<string | undefined>(undefined)
  const archivedFilter = ref(false)
  const clientStateIdFilter = ref<string | undefined>(undefined)
  /**
   * Restrict the overview to exactly these cliënten — a worklist somebody else
   * worked out, such as the cliënten behind one declaratie-oorzaak (#4553).
   *
   * Unlike every other filter here it is **not** the user's own choice made on
   * this screen, so `ClientsView` drives it from the route and nothing else: it
   * is what the URL says on every navigation, empty included. A restriction that
   * outlived the link that set it would be a cliëntenlijst quietly missing rows.
   */
  const clientIdsFilter = ref<string[]>([])

  // Per-state counts (non-archived, full visibility scope) for status tabs / summary
  const statusCounts = ref<ClientStatusCount[]>([])

  // Computed
  const hasClients = computed(() => clients.value.length > 0)
  const sortString = computed(() => `${sortField.value},${sortDirection.value}`)

  /**
   * Fetch clients with current filters and pagination
   */
  async function fetchClients(page?: number) {
    loading.value = true
    error.value = null

    if (page !== undefined) {
      currentPage.value = page
    }

    try {
      const api = initApiService()
      const response = await api.clients.listClients(
        currentPage.value,
        pageSize.value,
        sortString.value,
        searchQuery.value || undefined,
        locationIdFilter.value,
        archivedFilter.value,
        clientStateIdFilter.value,
        clientIdsFilter.value
      )

      const data = response.data as ClientListResponse
      clients.value = data.content ?? []
      totalElements.value = data.totalElements
      totalPages.value = data.totalPages
      currentPage.value = data.page
      pageSize.value = data.size
      totalUnfilteredCount.value = data.totalUnfilteredCount
      statusCounts.value = data.statusCounts ?? []
    } catch (err: unknown) {
      error.value = extractApiErrorMessage(err, t('errors.loadClients'))
      logUnexpectedFailure('Failed to fetch clients:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Get a single client by ID
   */
  async function getClient(clientId: string): Promise<ClientProfile> {
    try {
      const api = initApiService()
      const response = await api.clients.getClientById(clientId)
      return response.data as ClientProfile
    } catch (err: unknown) {
      // A behandelaar opening a dossier on mobile routinely loses the
      // connection mid-request. The error is rethrown and the view renders a
      // "connection lost" message, so logging it here only ships a Sentry
      // event via the console integration (SCRIVIO-FE-5W).
      if (!isNetworkError(err)) {
        logUnexpectedFailure('Failed to fetch client:', err)
      }
      throw err
    }
  }

  /**
   * Create a new client
   */
  async function createClient(data: CreateClientRequest): Promise<ClientProfile> {
    try {
      const api = initApiService()
      const response = await api.clients.createClient(data)
      const newClient = response.data as ClientProfile

      // Refresh the list to include the new client
      await fetchClients(0)

      return newClient
    } catch (err: unknown) {
      // Only log unexpected errors to console (which sends them to Sentry).
      // 409 Conflict (e.g., duplicate BSN) is an expected user validation error.
      if (!isConflictError(err)) {
        logUnexpectedFailure('Failed to create client:', err)
      }
      throw err
    }
  }

  /**
   * Update an existing client
   */
  async function updateClient(
    clientId: string,
    data: UpdateClientRequest
  ): Promise<ClientProfile> {
    try {
      const api = initApiService()
      const response = await api.clients.updateClient(clientId, data)
      const updatedClient = response.data as ClientProfile

      // Update in local state
      const index = clients.value.findIndex((c) => c.id === clientId)
      if (index !== -1) {
        clients.value[index] = updatedClient
      }

      return updatedClient
    } catch (err: unknown) {
      logUnexpectedFailure('Failed to update client:', err)
      throw err
    }
  }

  /**
   * Soft delete a client
   */
  async function deleteClient(clientId: string): Promise<void> {
    try {
      const api = initApiService()
      await api.clients.deleteClient(clientId)

      // Remove from local state
      clients.value = clients.value.filter((c) => c.id !== clientId)
      totalElements.value -= 1
    } catch (err: unknown) {
      logUnexpectedFailure('Failed to delete client:', err)
      throw err
    }
  }

  /**
   * Archive a client
   */
  async function archiveClient(clientId: string): Promise<ClientProfile> {
    try {
      const api = initApiService()
      const response = await api.clients.archiveClient(clientId)
      const archivedClient = response.data as ClientProfile

      // Update in local state or remove if not showing archived
      if (!archivedFilter.value) {
        clients.value = clients.value.filter((c) => c.id !== clientId)
      } else {
        const index = clients.value.findIndex((c) => c.id === clientId)
        if (index !== -1) {
          clients.value[index] = archivedClient
        }
      }

      return archivedClient
    } catch (err: unknown) {
      logUnexpectedFailure('Failed to archive client:', err)
      throw err
    }
  }

  /**
   * Send the client the cliëntportaal invitation mail (#4718). Answers the profile with
   * the new `portalInvitedAt`; a dossier without an e-mailadres is refused with a 422.
   */
  async function sendPortalInvitation(clientId: string): Promise<ClientProfile> {
    try {
      const api = initApiService()
      const response = await api.clients.sendClientPortalInvitation(clientId)
      return response.data
    } catch (err: unknown) {
      logUnexpectedFailure('Failed to send client portal invitation:', err)
      throw err
    }
  }

  /**
   * Unarchive a client
   */
  async function unarchiveClient(clientId: string): Promise<ClientProfile> {
    try {
      const api = initApiService()
      const response = await api.clients.unarchiveClient(clientId)
      const unarchivedClient = response.data as ClientProfile

      // Update in local state
      const index = clients.value.findIndex((c) => c.id === clientId)
      if (index !== -1) {
        clients.value[index] = unarchivedClient
      }

      return unarchivedClient
    } catch (err: unknown) {
      logUnexpectedFailure('Failed to unarchive client:', err)
      throw err
    }
  }

  /**
   * Get behandelaars assigned to a client
   */
  async function getClientBehandelaars(clientId: string): Promise<ClientBehandelaarResponse[]> {
    try {
      const api = initApiService()
      const response = await api.clients.getClientBehandelaars(clientId)
      return response.data as ClientBehandelaarResponse[]
    } catch (err: unknown) {
      logUnexpectedFailure('Failed to fetch client behandelaars:', err)
      throw err
    }
  }

  /**
   * Assign a behandelaar to a client's treatment team
   */
  async function assignBehandelaar(clientId: string, userId: string): Promise<ClientBehandelaarResponse> {
    try {
      const api = initApiService()
      const response = await api.clients.assignBehandelaar(clientId, userId)
      return response.data as ClientBehandelaarResponse
    } catch (err: unknown) {
      logUnexpectedFailure('Failed to assign behandelaar:', err)
      throw err
    }
  }

  /**
   * Unassign a behandelaar from a client's treatment team
   */
  async function unassignBehandelaar(clientId: string, userId: string): Promise<void> {
    try {
      const api = initApiService()
      await api.clients.unassignBehandelaar(clientId, userId)
    } catch (err: unknown) {
      logUnexpectedFailure('Failed to unassign behandelaar:', err)
      throw err
    }
  }

  /**
   * Set current page and fetch clients
   */
  async function setPage(page: number) {
    currentPage.value = page
    await fetchClients()
  }

  /**
   * Set sort field and direction, then fetch clients
   */
  async function setSort(field: string, direction: 'asc' | 'desc') {
    sortField.value = field
    sortDirection.value = direction
    currentPage.value = 0 // Reset to first page
    await fetchClients()
  }

  /**
   * Set search query and fetch clients
   */
  async function setSearch(query: string) {
    searchQuery.value = query
    currentPage.value = 0 // Reset to first page
    await fetchClients()
  }

  /**
   * Set filters and fetch clients
   */
  async function setFilters(filters: {
    locationId?: string
    archived?: boolean
    clientStateId?: string
  }) {
    if (filters.locationId !== undefined) {
      locationIdFilter.value = filters.locationId
    }
    if (filters.archived !== undefined) {
      archivedFilter.value = filters.archived
    }
    if (filters.clientStateId !== undefined) {
      // Empty string clears the state filter ("Alle" tab)
      clientStateIdFilter.value = filters.clientStateId || undefined
    }
    currentPage.value = 0 // Reset to first page
    await fetchClients()
  }

  /**
   * Restrict the overview to a set of cliënten, or lift the restriction with an
   * empty list. Separate from {@link setFilters} because the route owns it, not
   * a control on the page.
   *
   * Fetches nothing: it is applied by the load `ClientsView` already makes when
   * the route it came from changes, and a second one would page the same list
   * twice.
   */
  function restrictToClientIds(ids: string[]) {
    clientIdsFilter.value = ids
    currentPage.value = 0
  }

  /**
   * Clear all filters and search
   */
  async function clearFilters() {
    searchQuery.value = ''
    locationIdFilter.value = undefined
    archivedFilter.value = false
    clientStateIdFilter.value = undefined
    clientIdsFilter.value = []
    currentPage.value = 0
    await fetchClients()
  }

  /**
   * Clear all client data (on logout)
   */
  function clearClients() {
    clients.value = []
    totalElements.value = 0
    totalPages.value = 0
    currentPage.value = 0
    pageSize.value = 20
    totalUnfilteredCount.value = 0
    searchQuery.value = ''
    sortField.value = 'lastName'
    sortDirection.value = 'asc'
    locationIdFilter.value = undefined
    archivedFilter.value = false
    clientStateIdFilter.value = undefined
    clientIdsFilter.value = []
    statusCounts.value = []
    error.value = null
    loading.value = false
  }

  return {
    // State - Clients
    clients,
    loading,
    error,

    // State - Pagination
    totalElements,
    totalPages,
    currentPage,
    pageSize,
    totalUnfilteredCount,

    // State - Search and filters
    searchQuery,
    sortField,
    sortDirection,
    locationIdFilter,
    archivedFilter,
    clientStateIdFilter,
    clientIdsFilter,
    statusCounts,

    // Computed
    hasClients,
    sortString,

    // Actions - CRUD
    fetchClients,
    getClient,
    createClient,
    updateClient,
    deleteClient,
    archiveClient,
    sendPortalInvitation,
    unarchiveClient,
    getClientBehandelaars,
    assignBehandelaar,
    unassignBehandelaar,

    // Actions - Search, filter, pagination
    setPage,
    setSort,
    setSearch,
    setFilters,
    restrictToClientIds,
    clearFilters,
    clearClients
  }
})
